import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Loader2, FlaskConical, Users, Calculator, RefreshCw } from 'lucide-react';

const CATEGORY_COLORS = {
  'Sauce': 'bg-rose-100 text-rose-700 border-rose-200',
  'Marinade': 'bg-amber-100 text-amber-700 border-amber-200',
  'Base': 'bg-blue-100 text-blue-700 border-blue-200',
  'Topping': 'bg-purple-100 text-purple-700 border-purple-200',
  'Garniture': 'bg-green-100 text-green-700 border-green-200',
  'Autre': 'bg-slate-100 text-slate-700 border-slate-200',
};

const UNITES = ['g', 'mL', 'kg', 'L', 'cl', 'unité', 'pièce', 'sachet'];

// Unités convertibles vers g/mL pour normaliser les prix
const UNITE_TO_GRAM = { kg: 1000, g: 1, l: 1000, L: 1000, cl: 10, ml: 1, mL: 1 };

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 4 }).format(n || 0);
const fmtTiny = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 6 }).format(n || 0);

export function PreparationsTab() {
  const { api } = useAuth();
  const [preparations, setPreparations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mercurialeItems, setMercurialeItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({
    nom: '', categorie: 'Sauce', nb_portions: 10, unite_portion: 'portion',
    ingredients: [], notes: '',
  });
  const [newIng, setNewIng] = useState({ nom: '', quantite: '', unite: 'g', cout_unitaire: '' });
  const [showSuggest, setShowSuggest] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes, mRes] = await Promise.all([
        api.get('/preparations'),
        api.get('/preparations/categories'),
        api.get('/mercuriale/produits'),
      ]);
      setPreparations(pRes.data);
      setCategories(cRes.data);
      setMercurialeItems(mRes.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ nom: '', categorie: 'Sauce', nb_portions: 10, unite_portion: 'portion', ingredients: [], notes: '' });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditItem(p);
    setForm({
      nom: p.nom, categorie: p.categorie, nb_portions: p.nb_portions,
      unite_portion: p.unite_portion || 'portion',
      ingredients: [...(p.ingredients || [])],
      notes: p.notes || '',
    });
    setDialogOpen(true);
  };

  const addIngredient = () => {
    if (!newIng.nom.trim()) { toast.error("Nom requis"); return; }
    setForm(f => ({
      ...f,
      ingredients: [...f.ingredients, {
        nom: newIng.nom.trim(),
        quantite: parseFloat(newIng.quantite) || 0,
        unite: newIng.unite,
        cout_unitaire: parseFloat(newIng.cout_unitaire) || 0,
      }],
    }));
    setNewIng({ nom: '', quantite: '', unite: 'g', cout_unitaire: '' });
    setShowSuggest(false);
  };

  const removeIngredient = (idx) => {
    setForm(f => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));
  };

  const handleIngredientNameChange = (val) => {
    setNewIng(p => ({ ...p, nom: val }));
    setShowSuggest(true);
  };

  // Suggest TOUS les items Mercuriale, filtrés par nom si saisie >= 1 char
  const filteredSuggest = (newIng.nom.trim().length === 0
    ? mercurialeItems
    : mercurialeItems.filter(m => m.nom.toLowerCase().includes(newIng.nom.toLowerCase()))
  ).slice(0, 8);

  const pickSuggest = (m) => {
    // Détecte si le produit Mercuriale est en poids/volume ou en pièce
    const u = (m.unite || '').toLowerCase().trim();
    const isWeightOrVolume = u in UNITE_TO_GRAM;

    if (isWeightOrVolume && m.prix_au_g) {
      // Auto-conversion : on travaille en g (poids) ou mL (volume)
      const isVolume = ['l', 'cl', 'ml'].includes(u);
      setNewIng({
        nom: m.nom,
        quantite: '',
        unite: isVolume ? 'mL' : 'g',
        cout_unitaire: m.prix_au_g,  // €/g ou €/mL
      });
    } else {
      // Pièce / sachet / etc.
      setNewIng({
        nom: m.nom,
        quantite: '',
        unite: m.unite || 'unité',
        cout_unitaire: m.prix_unitaire || 0,
      });
    }
    setShowSuggest(false);
  };

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Nom requis'); return; }
    if (!form.nb_portions || form.nb_portions <= 0) { toast.error('Nombre de portions requis'); return; }
    if (form.ingredients.length === 0) { toast.error('Ajoute au moins 1 ingrédient'); return; }
    const payload = {
      ...form,
      nb_portions: parseFloat(form.nb_portions),
    };
    try {
      if (editItem) {
        await api.put(`/preparations/${editItem.id}`, payload);
        toast.success('Préparation mise à jour + Mercuriale synchronisée');
      } else {
        await api.post('/preparations', payload);
        toast.success('Préparation créée + ajoutée à la Mercuriale');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer "${p.nom}" ? L'entrée Mercuriale liée sera aussi supprimée.`)) return;
    try {
      await api.delete(`/preparations/${p.id}`);
      toast.success('Préparation supprimée');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleSyncAll = async () => {
    try {
      const res = await api.post('/preparations/sync-all');
      toast.success(`${res.data.synced} préparation(s) synchronisée(s) vers la Mercuriale`);
    } catch { toast.error('Erreur sync'); }
  };

  // Calculs en direct
  const coutTotal = form.ingredients.reduce((s, i) => s + (i.quantite || 0) * (i.cout_unitaire || 0), 0);
  const coutParPortion = form.nb_portions > 0 ? coutTotal / form.nb_portions : 0;

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4" data-testid="preparations-tab">
      <Card className="bg-blue-50/40 border-blue-200">
        <CardContent className="pt-4 pb-3 flex items-center gap-3">
          <FlaskConical className="w-8 h-8 text-blue-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold">Préparations / Sous-recettes</p>
            <p className="text-xs text-muted-foreground">
              Crée tes sauces, marinades et bases avec leur coût par portion. Chaque préparation est automatiquement
              ajoutée à la Mercuriale (€/portion) et utilisable comme ingrédient unique dans tes plats.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={handleSyncAll} className="gap-1" data-testid="sync-all-prep">
            <RefreshCw className="w-3 h-3" /> Resync
          </Button>
          <Button size="sm" onClick={openCreate} className="gap-1" data-testid="add-prep-btn">
            <Plus className="w-3 h-3" /> Nouvelle préparation
          </Button>
        </CardContent>
      </Card>

      {preparations.length === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <FlaskConical className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Aucune préparation</h3>
          <p className="text-muted-foreground mb-4 text-sm">
            Crée ta première sauce, marinade ou base avec son nombre de portions.<br/>
            Le coût unitaire sera automatiquement injecté dans la Mercuriale.
          </p>
          <Button onClick={openCreate} className="gap-1"><Plus className="w-4 h-4" /> Nouvelle préparation</Button>
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Préparation</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead className="text-right">Portions</TableHead>
                <TableHead className="text-right">Coût total</TableHead>
                <TableHead className="text-right">€ / portion</TableHead>
                <TableHead className="text-center">Ingrédients</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preparations.map(p => {
                const updateField = async (field, val) => {
                  try {
                    await api.put(`/preparations/${p.id}`, { [field]: val });
                    fetchData();
                  } catch { toast.error('Erreur sauvegarde'); }
                };
                return (
                <TableRow key={p.id} data-testid={`prep-row-${p.id}`}>
                  <TableCell>
                    <div className="font-semibold">{p.nom}</div>
                    {p.notes && <div className="text-xs text-muted-foreground line-clamp-1">{p.notes}</div>}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={CATEGORY_COLORS[p.categorie] || CATEGORY_COLORS['Autre']}>
                      {p.categorie}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <div className="flex items-center justify-end gap-1">
                      <Users className="w-3 h-3 text-muted-foreground" />
                      <Input type="number" step="1" defaultValue={p.nb_portions}
                        className="h-8 w-16 text-right tabular-nums"
                        onBlur={e => {
                          const v = parseFloat(e.target.value) || 1;
                          if (v !== p.nb_portions) updateField('nb_portions', v);
                        }}
                        data-testid={`prep-portions-${p.id}`} />
                      <span className="text-xs text-muted-foreground">{p.unite_portion}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(p.cout_total)}</TableCell>
                  <TableCell className="text-right tabular-nums font-bold text-blue-700">
                    {fmt(p.cout_par_portion)}
                  </TableCell>
                  <TableCell className="text-center text-xs text-muted-foreground">
                    {p.ingredients?.length || 0}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)} data-testid={`edit-prep-${p.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(p)} data-testid={`del-prep-${p.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}

      {/* DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prep-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-blue-600" />
              {editItem ? 'Modifier' : 'Nouvelle'} préparation
            </DialogTitle>
            <CardDescription>
              Définis ta sauce / base / marinade. Le coût par portion est calculé automatiquement
              et injecté dans la Mercuriale.
            </CardDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label>Nom de la préparation *</Label>
                <Input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                  placeholder="Ex: Sauce Poke, Marinade Saumon, Base Tapioca…" data-testid="prep-nom" />
              </div>
              <div className="space-y-1.5">
                <Label>Catégorie</Label>
                <Select value={form.categorie} onValueChange={v => setForm(f => ({ ...f, categorie: v }))}>
                  <SelectTrigger data-testid="prep-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" /> Nombre de portions *
                </Label>
                <div className="flex gap-2">
                  <Input type="number" min="1" step="1" value={form.nb_portions}
                    onChange={e => setForm(f => ({ ...f, nb_portions: e.target.value }))}
                    placeholder="20" data-testid="prep-portions" />
                  <Input value={form.unite_portion}
                    onChange={e => setForm(f => ({ ...f, unite_portion: e.target.value }))}
                    placeholder="portion" className="w-32" data-testid="prep-unite-portion" />
                </div>
              </div>
            </div>

            <Separator />

            {/* Ingrédients */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Ingrédients</Label>
              {form.ingredients.length > 0 && (
                <div className="space-y-1.5">
                  {form.ingredients.map((ing, idx) => {
                    const updateIng = (field, val) => {
                      setForm(f => ({
                        ...f,
                        ingredients: f.ingredients.map((it, i) => i === idx ? { ...it, [field]: val } : it),
                      }));
                    };
                    return (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-muted/40 rounded text-sm" data-testid={`prep-ing-row-${idx}`}>
                        <Input
                          className="flex-1 h-7 text-sm bg-background"
                          value={ing.nom}
                          onChange={e => updateIng('nom', e.target.value)}
                        />
                        <Input
                          type="number"
                          step={ing.unite === 'g' || ing.unite === 'mL' ? '1' : '0.01'}
                          className="w-20 h-7 text-sm tabular-nums bg-background"
                          value={ing.quantite}
                          onChange={e => updateIng('quantite', parseFloat(e.target.value) || 0)}
                        />
                        <Select value={ing.unite} onValueChange={v => updateIng('unite', v)}>
                          <SelectTrigger className="w-20 h-7 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {UNITES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          step="0.0001"
                          className="w-24 h-7 text-sm tabular-nums bg-background"
                          value={ing.cout_unitaire}
                          onChange={e => updateIng('cout_unitaire', parseFloat(e.target.value) || 0)}
                        />
                        <span className="font-bold tabular-nums w-20 text-right">
                          {fmt(ing.quantite * ing.cout_unitaire)}
                        </span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeIngredient(idx)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add ingredient */}
              <div className="grid grid-cols-12 gap-1.5 items-end">
                <div className="col-span-5 relative">
                  <Label className="text-xs">Nom (auto-complete Mercuriale)</Label>
                  <Input value={newIng.nom}
                    onChange={e => handleIngredientNameChange(e.target.value)}
                    onFocus={() => setShowSuggest(true)}
                    onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
                    placeholder={`Tape ou clique pour voir les ${mercurialeItems.length} produits Mercuriale`}
                    data-testid="prep-ing-nom" />
                  {showSuggest && filteredSuggest.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full bg-background border rounded shadow-md max-h-60 overflow-y-auto">
                      {filteredSuggest.map(s => {
                        const u = (s.unite || '').toLowerCase();
                        const isWV = u in UNITE_TO_GRAM;
                        const targetUnit = isWV ? (['l', 'cl', 'ml'].includes(u) ? 'mL' : 'g') : (s.unite || 'unité');
                        const displayPrice = isWV && s.prix_au_g
                          ? `${fmtTiny(s.prix_au_g)}/${targetUnit}`
                          : `${fmt(s.prix_unitaire)}/${s.unite || 'unité'}`;
                        return (
                          <button key={s.id} type="button"
                            onMouseDown={() => pickSuggest(s)}
                            className="w-full text-left px-2 py-1.5 hover:bg-blue-50 text-xs border-b last:border-b-0">
                            <div className="font-medium flex items-center justify-between">
                              <span>{s.nom}</span>
                              <span className="text-blue-600 tabular-nums">{displayPrice}</span>
                            </div>
                            <div className="text-muted-foreground text-[10px]">
                              {s.fournisseur || '—'}
                              {s.prix_unitaire ? ` · ${fmt(s.prix_unitaire)}/${s.unite}` : ''}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {showSuggest && filteredSuggest.length === 0 && newIng.nom.trim().length > 0 && (
                    <div className="absolute z-50 mt-1 w-full bg-background border rounded shadow-md p-2 text-xs text-muted-foreground">
                      Aucun produit Mercuriale ne correspond à "{newIng.nom}". Saisis le coût manuellement.
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Quantité</Label>
                  <Input type="number" step="0.01" value={newIng.quantite}
                    onChange={e => setNewIng(p => ({ ...p, quantite: e.target.value }))}
                    placeholder={newIng.unite === 'g' ? '500' : newIng.unite === 'mL' ? '250' : '1'}
                    data-testid="prep-ing-qty" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Unité</Label>
                  <Select value={newIng.unite} onValueChange={v => setNewIng(p => ({ ...p, unite: v }))}>
                    <SelectTrigger data-testid="prep-ing-unite"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {UNITES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">
                    € / {newIng.unite === 'g' || newIng.unite === 'mL' ? newIng.unite : 'unité'}
                  </Label>
                  <Input type="number" step="0.0001" value={newIng.cout_unitaire}
                    onChange={e => setNewIng(p => ({ ...p, cout_unitaire: e.target.value }))}
                    placeholder={newIng.unite === 'g' ? '0.005' : newIng.unite === 'mL' ? '0.003' : '5.00'}
                    data-testid="prep-ing-cout" />
                </div>
                <div className="col-span-1">
                  <Button onClick={addIngredient} size="icon" className="h-9 w-9" data-testid="prep-ing-add">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <Separator />

            {/* Live calc */}
            <Card className="bg-blue-50/50 border-blue-200">
              <CardContent className="pt-3 pb-3">
                <div className="flex items-center gap-3">
                  <Calculator className="w-5 h-5 text-blue-600 shrink-0" />
                  <div className="flex-1 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Coût total</p>
                      <p className="font-bold tabular-nums">{fmt(coutTotal)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Coût / portion ({form.nb_portions || 0} {form.unite_portion})</p>
                      <p className="font-bold text-lg text-blue-700 tabular-nums" data-testid="prep-cout-portion">
                        {fmt(coutParPortion)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-1.5">
              <Label>Notes (optionnel)</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Recette, conseils, DLC…" rows={2} data-testid="prep-notes" />
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
            <Button onClick={handleSave} data-testid="prep-save-btn">
              {editItem ? 'Enregistrer' : 'Créer + ajouter à la Mercuriale'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
